import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { GenericForm } from 'src/components';
import { ModalContainer } from 'src/components/ModalContainer';
import { useWorkflowVersionMutations } from 'src/graphql/entities/workflow';
import {
  CreateDraftWorkflowVersionField,
  type CreateDraftWorkflowVersionFormValues,
} from 'src/pages/Settings/workflows/components/create-draft-version-form';
import { CreateDraftWorkflowVersionFields } from 'src/pages/Settings/workflows/components/create-draft-version-form-fields';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { parseJsonSafely } from 'src/utils/objects';

const DEFAULT_WORKFLOW_DEFINITION = '{\n  "nodes": [],\n  "edges": []\n}';

export const CreateDraftWorkflowVersionModal = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showSuccessAlert } = useNotifications();
  const { closeModal } = useModalMethods(Modals.CreateDraftWorkflowVersion);
  const { isOpened, modalProps } = useModalState(Modals.CreateDraftWorkflowVersion);
  const { createDraftWorkflowVersion, loading } = useWorkflowVersionMutations({
    partnerId: selectedPartnerId,
    refetchWorkflowVersions: modalProps?.refetchWorkflowVersions,
  });

  const workflows = modalProps?.workflows ?? [];
  const initialDefinition = modalProps?.initialDefinition;
  const isSubmitting = loading.isCreatingDraft;

  const defaultValues = useMemo<CreateDraftWorkflowVersionFormValues>(() => {
    const parsedInitialDefinition = initialDefinition
      ? parseJsonSafely<Record<string, unknown>>(initialDefinition)
      : null;

    return {
      [CreateDraftWorkflowVersionField.WorkflowId]: modalProps?.workflowId ?? '',
      [CreateDraftWorkflowVersionField.Description]: '',
      [CreateDraftWorkflowVersionField.Definition]: parsedInitialDefinition
        ? JSON.stringify(parsedInitialDefinition, null, 2)
        : initialDefinition?.trim() || DEFAULT_WORKFLOW_DEFINITION,
    };
  }, [initialDefinition, modalProps?.workflowId]);

  const formMethods = useForm<CreateDraftWorkflowVersionFormValues>({
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (!isOpened) {
      return;
    }

    formMethods.reset(defaultValues);
  }, [defaultValues, formMethods, isOpened]);

  const handleClose = useCallback(() => {
    formMethods.reset(defaultValues);
    closeModal();
  }, [closeModal, defaultValues, formMethods]);

  const onSubmit = useCallback(
    async (values: CreateDraftWorkflowVersionFormValues) => {
      const result = await createDraftWorkflowVersion({
        workflowId: values[CreateDraftWorkflowVersionField.WorkflowId],
        definition: values[CreateDraftWorkflowVersionField.Definition],
        description: values[CreateDraftWorkflowVersionField.Description].trim() || undefined,
      });

      if (!result) {
        return;
      }

      showSuccessAlert(`Draft workflow version created successfully (v${result.version})`);
      modalProps?.onCreated?.(result);
      handleClose();
    },
    [createDraftWorkflowVersion, handleClose, modalProps, showSuccessAlert],
  );

  return (
    <ModalContainer
      open={isOpened}
      size="large"
      title="Create Draft Workflow Version"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'create-draft-version-cancel' },
        {
          label: 'Create draft',
          kind: 'primary',
          onClick: formMethods.handleSubmit(onSubmit),
          disabled: workflows.length === 0 || !formMethods.formState.isValid,
          loading: isSubmitting,
          testId: 'create-draft-version-submit',
        },
      ]}
      onClose={handleClose}
      data-testid="create-draft-version-modal"
    >
      <GenericForm<CreateDraftWorkflowVersionFormValues> formMethods={formMethods}>
        <CreateDraftWorkflowVersionFields disabled={isSubmitting} workflows={workflows} />
      </GenericForm>
    </ModalContainer>
  );
};
